import { useQuery } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'

import {
  getData,
  postData,
  updateFromData,
  deleteData,
  updateData,
} from '../../apis/api.helpers'
import apiUrl from '../../apis/api.url'
import { QueryParams } from '../../common/types'
import { useSnackbarManager } from '../../components/common/snackbar'
import { getErrorMessage, parseQueryParams } from '../../utilities/parsers'
import { useAuthStore } from '../../store/authStore'

// Disable non-login APIs (employees, groups) for this build
export const DISABLE_NONLOGIN_APIS = false

const buildUrlWithParams = (
  baseUrl: string,
  params: QueryParams | Record<string, any>
) => {
  return `${baseUrl}${parseQueryParams(params)}`
}

const getSuccessMessage = (response: any, fallback: string) => {
  const payload = response?.data ?? response
  return payload?.message || response?.message || fallback
}

const fetchData = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.ADMIN_USER, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.users || [],
    total: response?.meta?.total_count ?? 0,
    total_pages: response?.meta?.total_pages ?? 1,
    current_page: response?.meta?.current_page ?? 1,
  }
}

const fetchNutritionistUsers = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.NUTRITIONIST_USER, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.clients || [],
    total: response?.meta?.total_count ?? 0,
    total_pages: response?.meta?.total_pages ?? 1,
    current_page: response?.meta?.current_page ?? 1,
  }
}

export const useAdminUser = (input: QueryParams) => {
  const roleName = useAuthStore((s) => s.roleData?.name?.toLowerCase?.())
  return useQuery(
    ['admin_user_list', input, roleName],
    () =>
      ['nutritionist', 'physiotherapist', 'yogist'].includes(roleName || '')
        ? fetchNutritionistUsers(input)
        : fetchData(input),
    {
      enabled: !DISABLE_NONLOGIN_APIS,
    }
  )
}

export const yogaOverridesBulk = (
  subscriptionId: string | number,
  payload: {
    yoga_plan_id?: number | string
    day_number?: number | string
    date?: string
    exercises: Array<{
      yoga_id: number | string
      sequence_number: number
      reps?: number
    }>
  }
) => {
  return postData(
    `${apiUrl.SUBSCRIPTIONS}/${subscriptionId}/user_specific_yogas`,
    payload
  )
}

export const deActivateAdmin = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/deactivate`, {})
}
export const activateAdmin = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/activate`, {})
}
export const deleteAdmin = (id?: string) => {
  return deleteData(`${apiUrl.ADMIN_USER}/${id}`)
}

export const getAdminDetails = (id: string) => {
  return getData(`${apiUrl.ADMIN_USER}/${id}`)
}

export const getUsersFilterOptions = () => {
  return getData(`${apiUrl.ADMIN_USER}/filter_options`)
}

export const useUsersFilterOptions = (enabled = true) => {
  return useQuery(['users_filter_options'], getUsersFilterOptions, {
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export const getStaffActiveAssignments = (
  id: string | number,
  params?: {
    page?: number
    per_page?: number
    search?: string
    stage?: 'clients' | 'leads' | string
    type?: string
  }
) => {
  const queryParams = new URLSearchParams()
  if (params?.page) queryParams.append('page', String(params.page))
  if (params?.per_page) queryParams.append('per_page', String(params.per_page))
  if (params?.search) queryParams.append('search', params.search)
  if (params?.stage) queryParams.append('stage', params.stage)
  if (params?.type) queryParams.append('type', params.type)
  const queryString = queryParams.toString()
  return getData(
    `${apiUrl.ADMIN_USER}/${id}/active_assignments${queryString ? `?${queryString}` : ''}`
  )
}

export const reassignStaffAssignment = (
  id: string | number,
  payload: {
    assignment_id: string | number
    new_staff_id: string | number
    action_type?: string
    reason?: string
    notes?: string
    is_lead?: boolean
    assignment_type?: 'client' | 'lead' | string
    stage?: 'clients' | 'leads' | string
  }
) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/reassign_assignment`, payload)
}

export const bulkReassignStaffAssignments = (
  id: string | number,
  payload: {
    new_staff_id: string | number
    assignment_ids?: (string | number)[]
    client_ids?: (string | number)[]
    lead_ids?: (string | number)[]
    action_type?: string
    reason?: string
    notes?: string
    assignment_type?: 'client' | 'lead' | string
    stage?: 'clients' | 'leads' | string
  }
) => {
  return postData(
    `${apiUrl.ADMIN_USER}/${id}/bulk_reassign_assignments`,
    payload
  )
}

export const getActivePlanOverview = (
  id: string | number,
  subscriptionId?: string | number | null
) => {
  const q = subscriptionId
    ? `?subscription_id=${encodeURIComponent(String(subscriptionId))}`
    : ''
  return getData(
    `${apiUrl.SUBSCRIPTION_CALENDAR}/${id}/active_plan_overview${q}`
  )
}
export const getOverviewDetail = (
  id: string | number,
  date: string,
  subscriptionId?: string | number | null
) => {
  const params = new URLSearchParams()
  if (date) params.append('date', date)
  if (subscriptionId) params.append('subscription_id', String(subscriptionId))
  const qs = params.toString() ? `?${params.toString()}` : ''
  return getData(`${apiUrl.SUBSCRIPTION_CALENDAR}/${id}/active_plan_day${qs}`)
}
export const freezeSubscription = (
  id: string,
  payload?: { reason?: string; start_date?: string; end_date?: string }
) => {
  return postData(`${apiUrl.SUBSCRIPTIONS}/${id}/freeze`, payload)
}

export const unfreezeSubscription = (
  id: string | number,
  payload?: {
    reason?: string
    start_date?: string
    end_date?: string
    unfreeze_dates?: string[]
  }
) => {
  return postData(`${apiUrl.SUBSCRIPTIONS}/${id}/unfreeze`, payload)
}
export const workoutOverridesBulk = (
  subscriptionId: string | number,
  payload: {
    workout_plan_id?: number | string
    day_number?: number | string
    date?: string
    exercises: Array<{
      workout_id: number | string
      sequence_number: number
      reps: number
    }>
  }
) => {
  return postData(
    `${apiUrl.SUBSCRIPTIONS}/${subscriptionId}/user_specific_exercises`,
    payload
  )
}

export const meditationOverridesBulk = (
  subscriptionId: string | number,
  payload: {
    plan_id: number | string
    meditations: Array<{
      meditation_id: number | string
      sequence_number: number
    }>
  }
) => {
  return postData(
    `${apiUrl.SUBSCRIPTIONS}/${subscriptionId}/user_specific_meditations`,
    payload
  )
}

export const dietOverridesBulk = (
  subscriptionId: string | number,
  payload: {
    day_number?: number | string
    date?: string
    diet_plans: Array<{
      meal_time: string
      notes?: string
      meal_name?: string
      items: Array<{
        meal_id: number | string
        quantity: number
        requirement?: 'mandatory' | 'optional'
      }>
    }>
  }
) => {
  return postData(
    `${apiUrl.SUBSCRIPTIONS}/${subscriptionId}/user_specific_diet_plans`,
    payload
  )
}

export const assignDietPlanTemplate = (
  subscriptionId: string | number,
  payload: { diet_plan_template_id: number; start_date?: string }
) => {
  return postData(
    `${apiUrl.SUBSCRIPTIONS}/${subscriptionId}/assign_diet_plan_template`,
    payload
  )
}
export const freezeUser = (
  id: string,
  payload: { reason: string; start_date: string; end_date: string }
) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/freeze`, payload)
}

export const unfreezeUser = (id: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/unfreeze`, {})
}

export const createAdmin = (input: any) => {
  return postData(`${apiUrl.ADMIN_USER}`, input)
}
export const useCreateAdmin = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(createAdmin, {
    onSuccess: (res: any) => {
      const payload = res?.data ?? res
      handleSubmission(payload)
      const message = getSuccessMessage(res, 'Created successfully')
      enqueueSnackbar(message, { variant: 'success' })
    },

    onError: (error: any) => {
      const data = error?.response?.data as any
      const isDuplicateEmail =
        Array.isArray(data?.errors) &&
        data.errors.some((m: any) =>
          String(m || '')
            .toLowerCase()
            .includes('email has already been taken')
        )
      if (isDuplicateEmail) return

      enqueueSnackbar(
        getErrorMessage(
          data?.error || data?.detail || data?.errors || data?.message
        ),
        {
          variant: 'error',
        }
      )
    },
  })
}
export const updateTask = ({ id, data }: any) => {
  return updateFromData(`${apiUrl.ADMIN_USER}/${id}`, data)
}
export const useUpdateAdmin = (handleSubmission: (data: any) => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(updateTask, {
    onSuccess: (res: any) => {
      const payload = res?.data ?? res
      handleSubmission(payload)
      const message = getSuccessMessage(res, 'Updated successfully')

      enqueueSnackbar(message, { variant: 'success' })
    },

    onError: (error: any) => {
      const data = error?.response?.data as any
      const isDuplicateEmail =
        Array.isArray(data?.errors) &&
        data.errors.some((m: any) =>
          String(m || '')
            .toLowerCase()
            .includes('email has already been taken')
        )
      if (isDuplicateEmail) return

      // enqueueSnackbar(getErrorMessage(error.response.data.error), {
      //   variant: 'error',
      // })
      enqueueSnackbar(
        data?.detail
          ? getErrorMessage(data?.detail)
          : getErrorMessage(data?.error || data?.errors || data?.message),
        {
          variant: 'error',
        }
      )
    },
  })
}
export const getRoles = () => {
  return Promise.resolve({ items: [], total: 0 })
}
export const updatePassword = (employee: string, data: string) => {
  return updateFromData(
    `${apiUrl.ADMIN_USER}/${employee}/change_password`,
    data
  )
}

// Subscriptions
export const createSubscription = (payload: any) => {
  return postData(`${apiUrl.SUBSCRIPTIONS}`, payload)
}

export const sendAdminInvitation = (id?: string) => {
  return postData(`${apiUrl.ADMIN_USER}/${id}/invite`, {})
}

// Assigned Clients (for Nutritionist, Physiotherapist, Yogist)
const fetchAssignedClients = async (
  input: QueryParams & {
    admin_id?: string | number
    status?: string
    search?: string
  }
) => {
  const url = buildUrlWithParams(apiUrl.ASSIGNED_CLIENTS, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.assigned_clients || [],
    total: response?.meta?.total_count ?? 0,
    total_pages: response?.meta?.total_pages ?? 1,
    current_page: response?.meta?.current_page ?? 1,
  }
}

export const useAssignedClients = (
  input: QueryParams & {
    admin_id?: string | number
    status?: string
    search?: string
  }
) => {
  return useQuery(
    ['assigned_clients', input],
    () => fetchAssignedClients(input),
    {
      enabled: !!input?.admin_id,
    }
  )
}

export const createAssignedClient = (payload: {
  admin_id: number | string
  user_id: number | string
}) => {
  return postData(`${apiUrl.ASSIGNED_CLIENTS}`, {
    assigned_client: payload,
  } as any)
}

export const deleteAssignedClient = (id: string | number) => {
  return deleteData(`${apiUrl.ASSIGNED_CLIENTS}/${id}`)
}

// Body Measurements (Client)
const fetchBodyMeasurements = async (
  input: QueryParams & { user_id?: string | number }
) => {
  const url = buildUrlWithParams(apiUrl.BODY_MEASUREMENTS, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.body_measurements || [],
    total: response?.meta?.total_count ?? 0,
    total_pages: response?.meta?.total_pages ?? 1,
    current_page: response?.meta?.current_page ?? 1,
  }
}

export const useBodyMeasurements = (
  input: QueryParams & { user_id?: string | number }
) => {
  return useQuery(
    ['body_measurements', input],
    () => fetchBodyMeasurements(input),
    {
      enabled: !!input?.user_id,
    }
  )
}

// Body Compositions (Client)
const fetchBodyCompositions = async (
  input: QueryParams & { user_id?: string | number }
) => {
  const url = buildUrlWithParams(apiUrl.BODY_COMPOSITION, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.body_compositions || [],
    total: response?.meta?.total_count ?? 0,
    total_pages: response?.meta?.total_pages ?? 1,
    current_page: response?.meta?.current_page ?? 1,
  }
}

export const useBodyCompositions = (
  input: QueryParams & { user_id?: string | number }
) => {
  return useQuery(
    ['body_compositions', input],
    () => fetchBodyCompositions(input),
    {
      enabled: !!input?.user_id,
    }
  )
}

// Vitals (Client)
const fetchVitals = async (
  input: QueryParams & { user_id?: string | number }
) => {
  const url = buildUrlWithParams(apiUrl.VITALS, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.vitals || [],
    total: response?.meta?.total_count ?? 0,
    total_pages: response?.meta?.total_pages ?? 1,
    current_page: response?.meta?.current_page ?? 1,
  }
}

export const useVitals = (
  input: QueryParams & { user_id?: string | number }
) => {
  return useQuery(['vitals', input], () => fetchVitals(input), {
    enabled: !!input?.user_id,
  })
}

// Client Monthly Reports
const fetchClientReports = async (
  input: QueryParams & { user_id?: string | number }
) => {
  const url = buildUrlWithParams(apiUrl.CLIENT_REPORTS, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.monthly_reports || [],
    total: response?.meta?.total_count ?? 0,
    total_pages: response?.meta?.total_pages ?? 1,
    current_page: response?.meta?.current_page ?? 1,
  }
}

export const useClientReports = (
  input: QueryParams & { user_id?: string | number }
) => {
  return useQuery(['client_reports', input], () => fetchClientReports(input), {
    enabled: !!input?.user_id,
  })
}

export const createClientReport = (payload: {
  user_id: number | string
  month: number
  year: number
}) => {
  return postData(`${apiUrl.CLIENT_REPORTS}`, payload)
}

// User reminders
const fetchUserReminders = async (input: { user_id: string | number }) => {
  const url = buildUrlWithParams(apiUrl.USER_REMINDERS, {
    user_id: input.user_id,
  })

  const response: any = await getData(url)

  const items = Array.isArray(response?.reminders) ? response.reminders : []

  const meta = response?.meta ?? {}

  return { items, meta }
}

export const useUserReminders = ({ userId }: { userId?: string | number }) => {
  return useQuery(
    ['user_reminders', userId],
    () =>
      fetchUserReminders({
        user_id: userId!,
      }),
    {
      enabled: !!userId,
    }
  )
}

// User meal timings
export const updateUserMealTiming = (
  userId: string | number,
  payload: {
    user_meal_timing: {
      meal_time: string
      time: string
      diet_plan_template_id: number | string
      subscription_id: number | string
      sequence_number: number
    }
  }
) => {
  const url = `${apiUrl.USER_MEAL_TIMINGS}?user_id=${userId}`
  return postData(url, payload)
}

export const useUpdateUserMealTiming = (onSuccess?: () => void) => {
  const { enqueueSnackbar } = useSnackbarManager()
  return useMutation(
    ({
      userId,
      payload,
    }: {
      userId: string | number
      payload: Parameters<typeof updateUserMealTiming>[1]
    }) => updateUserMealTiming(userId, payload),
    {
      onSuccess: () => {
        enqueueSnackbar('Meal time updated successfully', {
          variant: 'success',
        })
        onSuccess?.()
      },
      onError: (error: any) => {
        enqueueSnackbar(
          getErrorMessage(error) || 'Failed to update meal time',
          { variant: 'error' }
        )
      },
    }
  )
}

// Subscription-level report
export const getSubscriptionReport = (subscriptionId: string | number) => {
  return getData(`${apiUrl.SUBSCRIPTIONS}/${subscriptionId}/report`)
}

export const useSubscriptionReport = (
  subscriptionId?: string | number | null,
  options?: { enabled?: boolean }
) => {
  return useQuery(
    ['subscription_report', subscriptionId],
    () => getSubscriptionReport(subscriptionId as string | number),
    {
      enabled: !!subscriptionId && (options?.enabled ?? true),
    }
  )
}

// User subscription history
const fetchUserSubscriptionHistory = async (
  input: QueryParams & { user_id?: string | number }
) => {
  const url = buildUrlWithParams(apiUrl.SUBSCRIPTION_HISTORY, {
    ...input,
  })
  const response = await getData(url)
  return response
}

export const useUserSubscriptionHistory = (
  userId?: string | number | null,
  options?: QueryParams
) => {
  return useQuery(
    ['user_subscription_history', userId, options],
    () =>
      fetchUserSubscriptionHistory({
        user_id: userId || '',
        page: options?.page || 1,
        ...options,
      }),
    {
      enabled: !!userId,
    }
  )
}

export const getUserAdditionalData = (
  userId: string | number,
  subscriptionId?: string | number | null
) => {
  const baseUrl = `${apiUrl.SUBSCRIPTION_CALENDAR}/${userId}/additional_data`
  const url =
    subscriptionId !== undefined &&
    subscriptionId !== null &&
    subscriptionId !== ''
      ? `${baseUrl}?subscription_id=${subscriptionId}`
      : baseUrl
  return getData(url)
}

export const saveUserAdditionalData = (
  userId: string | number,
  payload: Record<string, any>,
  subscriptionId?: string | number | null
) => {
  const baseUrl = `${apiUrl.SUBSCRIPTION_CALENDAR}/${userId}/additional_data`
  const url =
    subscriptionId !== undefined &&
    subscriptionId !== null &&
    subscriptionId !== ''
      ? `${baseUrl}?subscription_id=${subscriptionId}`
      : baseUrl
  return postData(url, {
    additional_data: payload,
  })
}

export const updateUserAdditionalData = (
  userId: string | number,
  payload: Record<string, any>,
  additionalDataId?: string | number | null
) => {
  const url = `${apiUrl.SUBSCRIPTION_CALENDAR}/${userId}/additional_data/${additionalDataId}`
  return updateData(url, {
    additional_data: payload,
  })
}

// Inactive Users
const fetchInactiveUsers = async (input: QueryParams) => {
  const url = buildUrlWithParams(apiUrl.INACTIVE_USERS, {
    ...input,
  })
  const response = await getData(url)
  return {
    items: response?.users || [],
    total: response?.pagination?.total_count ?? 0,
    total_pages: response?.pagination?.total_pages ?? 1,
    current_page: response?.pagination?.current_page ?? 1,
    threshold_days: response?.threshold_days ?? 5,
  }
}

export const useInactiveUsers = (input: QueryParams) => {
  return useQuery(
    ['inactive_users_list', input],
    () => fetchInactiveUsers(input),
    {
      enabled: !DISABLE_NONLOGIN_APIS,
    }
  )
}

export const getUserMarketingForms = (userId: string, params: any) => {
  const url = `${apiUrl.USER_MARKETING_FORMS}/${userId}/marketing_forms${parseQueryParams(params || {})}`
  return getData(url)
}

export const useUserMarketingForms = (userId: string, params: any) => {
  return useQuery(
    ['user_marketing_forms', userId, params],
    () => getUserMarketingForms(userId, params),
    { enabled: Boolean(userId) }
  )
}

export const getUserMarketingForm = (userId: string, formId: string) => {
  const url = `${apiUrl.USER_MARKETING_FORMS}/${userId}/marketing_forms/${formId}`
  return getData(url)
}

export const useUserMarketingForm = (
  userId: string | null,
  formId: string | null
) => {
  return useQuery(
    ['user_marketing_form', userId, formId],
    () => getUserMarketingForm(userId!, formId!),
    { enabled: Boolean(userId && formId) }
  )
}

// User Sales Leads (admin view)
export const useUserSalesLeads = (
  userId: string | number | undefined,
  params: Record<string, any>
) => {
  return useQuery(
    ['user_sales_leads', userId, params],
    () =>
      getData(
        `/admin/users/${userId}/sales_leads${parseQueryParams(params || {})}`
      ),
    { enabled: Boolean(userId) }
  )
}

// User Sales Clients (admin view)
export const useUserSalesClients = (
  userId: string | number | undefined,
  params: Record<string, any>
) => {
  return useQuery(
    ['user_sales_clients', userId, params],
    () =>
      getData(
        `/admin/users/${userId}/sales_clients${parseQueryParams(params || {})}`
      ),
    { enabled: Boolean(userId) }
  )
}

export const assignSalesToClient = (
  userId: string | number,
  salesRepId: string | number | null
) => postData(`/users/${userId}/assign_sales`, { sales_rep_id: salesRepId })

export const useActiveSalesTeam = () => {
  const { roleData } = useAuthStore()
  const roleName = String(roleData?.name || '').toLowerCase()
  const canFetchSales = roleName === 'admin' || roleName === 'superadmin'

  return useQuery(
    ['active_sales_team'],
    () => getData('/users?role=sales&status=active&per_page=100'),
    {
      staleTime: 60000,
      enabled: canFetchSales,
    }
  )
}

export const updateUserWaterIntake = (
  userId: string | number,
  proposedWaterIntake: number | null
) => {
  return postData(`/users/${userId}/update_water_intake`, {
    proposed_water_intake: proposedWaterIntake,
  })
}
